import apiClient from '../core';

/**
 * Brand package font configuration
 */
export interface BrandPackageFonts {
  headingFont: string;
  bodyFont: string;
  baseSize?: string;
}

/**
 * Brand package colors (same structure as theme colors)
 */
export interface BrandPackageColors {
  navbar?: Record<string, string>;
  navigationMenu?: Record<string, string>;
  hero?: Record<string, string>;
  features?: Record<string, string>;
  footer?: Record<string, string>;
  cta?: Record<string, string>;
  form?: Record<string, string>;
  pricing?: Record<string, string>;
  team?: Record<string, string>;
  mediaText?: Record<string, string>;
  textBlocks?: Record<string, string>;
  faq?: Record<string, string>;
  testimonials?: Record<string, string>;
  splitHero?: Record<string, string>;
  simpleHeader?: Record<string, string>;
  titleSubtitle?: Record<string, string>;
  contactInfo?: Record<string, string>;
  video?: Record<string, string>;
  countdownTimer?: Record<string, string>;
  imageCard?: Record<string, string>;
  imageGallery?: Record<string, string>;
  imageInfo?: Record<string, string>;
  htmlEmbed?: Record<string, string>;
  promoBanner?: Record<string, string>;
  logocloud?: Record<string, string>;
  stats?: Record<string, string>;
  blog?: Record<string, string>;
  [key: string]: Record<string, string> | undefined;
}

/**
 * Brand package response from API
 */
export interface BrandPackage {
  id: string;
  userId: string;
  name: string;
  description?: string;
  colors: BrandPackageColors;
  fonts: BrandPackageFonts;
  createdAt: string;
  updatedAt: string;
}

/**
 * Data for creating a brand package
 */
export interface CreateBrandPackageData {
  name: string;
  description?: string;
  colors: BrandPackageColors;
  fonts: BrandPackageFonts;
}

/**
 * Data for updating a brand package
 */
export interface UpdateBrandPackageData {
  name?: string;
  description?: string | null;
  colors?: BrandPackageColors;
  fonts?: Partial<BrandPackageFonts>;
}

export const brandPackagesApi = {
  /**
   * List all brand packages for the authenticated user
   */
  async list(): Promise<BrandPackage[]> {
    const response = await apiClient.get<BrandPackage[]>('/brand-packages');
    console.log('🎨 BrandPackagesAPI: List response:', response.data);
    return response.data;
  },

  /**
   * Get a single brand package by ID
   */
  async get(id: string): Promise<BrandPackage> {
    const response = await apiClient.get<BrandPackage>(`/brand-packages/${id}`);
    return response.data;
  },

  /**
   * Create a new brand package
   */
  async create(data: CreateBrandPackageData): Promise<BrandPackage> {
    const response = await apiClient.post<BrandPackage>('/brand-packages', data);
    console.log('🎨 BrandPackagesAPI: Created brand package:', response.data);
    return response.data;
  },

  /**
   * Update an existing brand package
   */
  async update(id: string, data: UpdateBrandPackageData): Promise<BrandPackage> {
    const response = await apiClient.put<BrandPackage>(`/brand-packages/${id}`, data);
    console.log('🎨 BrandPackagesAPI: Updated brand package:', response.data);
    return response.data;
  },

  /**
   * Delete a brand package
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/brand-packages/${id}`);
    console.log('🎨 BrandPackagesAPI: Deleted brand package:', id);
  },
};
